import { getExtension, getExtensionValue } from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  Extension,
  Location,
  Organization,
  Reference,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { buildTimezoneExtension } from "@/utils/fhir-timezone";

import { isValidTimezone } from "./is-valid-timezone";

/**
 * Reads the practice default timezone stored on the practice Organization.
 *
 * @param organization The practice Organization.
 * @returns The timezone coding when it holds a timezone code the runtime recognizes.
 */
export function getOrganizationDefaultTimezone(
  organization: Organization | undefined,
): Coding | undefined {
  const timezoneExtension = getExtensionValue(organization, Extensions.Timezone) as
    | CodeableConcept
    | undefined;
  const coding = timezoneExtension?.coding?.[0];

  return isValidTimezone(coding?.code) ? coding : undefined;
}

/**
 * Reads the Location that the practice default timezone was copied from.
 *
 * @param organization The practice Organization.
 * @returns The default Location reference, when one is set.
 */
export function getOrganizationDefaultLocationReference(
  organization: Organization | undefined,
): Reference<Location> | undefined {
  return getExtension(organization, Extensions.PracticeDefaultLocation)?.valueReference as
    | Reference<Location>
    | undefined;
}

/**
 * Stamps the practice default timezone onto an Organization. Every writer uses this, so they all
 * save the same shape.
 *
 * @param organization The practice Organization to update.
 * @param timezone The default timezone coding.
 * @param locationReference The Location the timezone was copied from. Omit it to keep the timezone
 * without a default Location, for example after that Location's hours were deleted.
 * @returns A copy of the Organization with the two extensions replaced and others left untouched.
 */
export function applyPracticeDefaultTimezone(
  organization: Organization,
  timezone: Coding,
  locationReference?: Reference<Location>,
): Organization {
  const otherExtensions = (organization.extension ?? []).filter(
    (extension) =>
      extension.url !== Extensions.Timezone && extension.url !== Extensions.PracticeDefaultLocation,
  );
  const defaultExtensions: Extension[] = [buildTimezoneExtension(timezone)];
  if (locationReference) {
    defaultExtensions.push({
      url: Extensions.PracticeDefaultLocation,
      valueReference: locationReference,
    });
  }

  return { ...organization, extension: [...otherExtensions, ...defaultExtensions] };
}
